import * as React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import Alert from '@mui/joy/Alert';
import Divider from '@mui/joy/Divider';
import {
  CakeRounded,
  FavoriteRounded,
  RefreshRounded,
  SearchRounded,
  OpenInNewRounded,
  FilterAltRounded,
  FilterAltOffRounded,
} from '@mui/icons-material';

import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import { fetchNityaSevaEnrollments, NityaSevaEnrollment } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

type CelebrationKind = 'birthday' | 'anniversary';

interface CelebrationEntry {
  enrollmentId: number;
  fullName: string;
  phone: string;
  whatsAppNumber: string;
  email: string;
  pledgeTier: string;
  pledgeAmount: number;
  status: string;
  kind: CelebrationKind;
  /** The stored raw date string, e.g. "1990-05-15" */
  originalDate: string;
  /** Next occurrence as a Date object */
  nextDate: Date;
  /** Calendar days from today (0 = today) */
  daysUntil: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeNextOccurrence(
  dateStr: string | undefined | null,
): { nextDate: Date; daysUntil: number } | null {
  if (!dateStr || dateStr.startsWith('0001-01-01')) return null;
  const parts = dateStr.split('-');
  if (parts.length < 3) return null;
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (!month || !day) return null;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const thisYear = todayStart.getFullYear();

  let next = new Date(thisYear, month - 1, day);
  if (next < todayStart) {
    next = new Date(thisYear + 1, month - 1, day);
  }
  const daysUntil = Math.round(
    (next.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24),
  );
  return { nextDate: next, daysUntil };
}

function buildCelebrations(enrollments: NityaSevaEnrollment[]): CelebrationEntry[] {
  const entries: CelebrationEntry[] = [];
  for (const e of enrollments) {
    const bdOcc = computeNextOccurrence(e.dateOfBirth);
    if (bdOcc) {
      entries.push({
        enrollmentId: e.id,
        fullName: e.fullName,
        phone: e.phone || e.whatsAppNumber,
        whatsAppNumber: e.whatsAppNumber,
        email: e.email,
        pledgeTier: e.pledgeTier,
        pledgeAmount: e.pledgeAmount,
        status: e.status,
        kind: 'birthday',
        originalDate: e.dateOfBirth,
        nextDate: bdOcc.nextDate,
        daysUntil: bdOcc.daysUntil,
      });
    }
    const annOcc = computeNextOccurrence(e.anniversaryDate);
    if (annOcc) {
      entries.push({
        enrollmentId: e.id,
        fullName: e.fullName,
        phone: e.phone || e.whatsAppNumber,
        whatsAppNumber: e.whatsAppNumber,
        email: e.email,
        pledgeTier: e.pledgeTier,
        pledgeAmount: e.pledgeAmount,
        status: e.status,
        kind: 'anniversary',
        originalDate: e.anniversaryDate!,
        nextDate: annOcc.nextDate,
        daysUntil: annOcc.daysUntil,
      });
    }
  }
  return entries.sort((a, b) => a.daysUntil - b.daysUntil);
}

const statusColor = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
  switch (status) {
    case 'active': return 'success';
    case 'pending': return 'warning';
    case 'paused': return 'neutral';
    case 'cancelled': return 'danger';
    default: return 'neutral';
  }
};

function daysLabel(d: number): { label: string; color: 'danger' | 'warning' | 'primary' | 'neutral' } {
  if (d === 0) return { label: 'Today 🎉', color: 'danger' };
  if (d === 1) return { label: 'Tomorrow', color: 'warning' };
  if (d <= 7) return { label: `In ${d} days`, color: 'warning' };
  if (d <= 30) return { label: `In ${d} days`, color: 'primary' };
  return { label: `In ${d} days`, color: 'neutral' };
}

const WINDOW_OPTIONS = [
  { label: 'Next 7 days', value: 7 },
  { label: 'Next 14 days', value: 14 },
  { label: 'Next 30 days', value: 30 },
  { label: 'Next 60 days', value: 60 },
  { label: 'Next 90 days', value: 90 },
  { label: 'All upcoming', value: 9999 },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Celebrations() {
  const navigate = useNavigate();

  // Raw data
  const [enrollments, setEnrollments] = React.useState<NityaSevaEnrollment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Filters
  const [windowDays, setWindowDays] = React.useState(30);
  const [kindFilter, setKindFilter] = React.useState<'all' | 'birthday' | 'anniversary'>('all');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [tierFilter, setTierFilter] = React.useState<string>('all');
  const [searchQ, setSearchQ] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchNityaSevaEnrollments({ limit: 2000 });
      setEnrollments(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // Build and filter celebrations
  const allCelebrations = React.useMemo(
    () => buildCelebrations(enrollments),
    [enrollments],
  );

  const uniqueTiers = React.useMemo(
    () => Array.from(new Set(enrollments.map((e) => e.pledgeTier).filter(Boolean))).sort(),
    [enrollments],
  );

  const filtered = React.useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return allCelebrations.filter((c) => {
      if (c.daysUntil > windowDays) return false;
      if (kindFilter !== 'all' && c.kind !== kindFilter) return false;
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (tierFilter !== 'all' && c.pledgeTier !== tierFilter) return false;
      if (q && !c.fullName.toLowerCase().includes(q) && !c.phone.includes(q) && !c.email.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [allCelebrations, windowDays, kindFilter, statusFilter, tierFilter, searchQ]);

  const todayBirthdays = allCelebrations.filter((c) => c.kind === 'birthday' && c.daysUntil === 0).length;
  const todayAnniversaries = allCelebrations.filter((c) => c.kind === 'anniversary' && c.daysUntil === 0).length;
  const upcomingInWindow = filtered.length;

  const hasActiveFilters =
    kindFilter !== 'all' || statusFilter !== 'all' || tierFilter !== 'all' || searchQ.trim() !== '' || windowDays !== 30;

  const clearFilters = () => {
    setKindFilter('all');
    setStatusFilter('all');
    setTierFilter('all');
    setSearchQ('');
    setWindowDays(30);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: {
            xs: 'calc(12px + var(--Header-height))',
            sm: 'calc(12px + var(--Header-height))',
            md: 3,
          },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 2,
          bgcolor: 'background.body',
        }}
      >
        {/* Breadcrumbs */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PageBreadcrumbs links={[]} current="Celebrations" />
        </Box>

        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            mb: 1,
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'start', sm: 'center' },
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CakeRounded sx={{ fontSize: 32, color: 'primary.500' }} />
            <Typography level="h2" component="h1">
              Celebrations
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="neutral"
            size="sm"
            startDecorator={<RefreshRounded />}
            onClick={load}
            loading={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert color="danger" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' },
            gap: 2,
            mb: 1,
          }}
        >
          <Card variant="soft" color="danger" invertedColors>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CakeRounded />
                <Typography level="title-sm">Today's Birthdays</Typography>
              </Box>
              <Typography level="h2">
                {loading ? <CircularProgress size="sm" /> : todayBirthdays}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="soft" color="warning" invertedColors>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <FavoriteRounded />
                <Typography level="title-sm">Today's Anniversaries</Typography>
              </Box>
              <Typography level="h2">
                {loading ? <CircularProgress size="sm" /> : todayAnniversaries}
              </Typography>
            </CardContent>
          </Card>

          <Card variant="soft" color="primary" invertedColors sx={{ gridColumn: { xs: 'span 2', sm: 'span 1' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <FilterAltRounded />
                <Typography level="title-sm">Upcoming (filtered)</Typography>
              </Box>
              <Typography level="h2">
                {loading ? <CircularProgress size="sm" /> : upcomingInWindow}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Filter bar */}
        <Sheet
          variant="outlined"
          sx={{ borderRadius: 'md', p: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            {/* Search */}
            <FormControl size="sm" sx={{ minWidth: 180, flex: 1 }}>
              <FormLabel>Search</FormLabel>
              <Input
                placeholder="Name, phone, email…"
                startDecorator={<SearchRounded />}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
              />
            </FormControl>

            {/* Window */}
            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <FormLabel>Time Window</FormLabel>
              <Select
                value={windowDays}
                onChange={(_, v) => v !== null && setWindowDays(v)}
              >
                {WINDOW_OPTIONS.map((o) => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
            </FormControl>

            {/* Kind */}
            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <FormLabel>Event Type</FormLabel>
              <Select
                value={kindFilter}
                onChange={(_, v) => v !== null && setKindFilter(v as typeof kindFilter)}
              >
                <Option value="all">All Events</Option>
                <Option value="birthday">Birthday Only</Option>
                <Option value="anniversary">Anniversary Only</Option>
              </Select>
            </FormControl>

            {/* Status */}
            <FormControl size="sm" sx={{ minWidth: 130 }}>
              <FormLabel>Enrollment Status</FormLabel>
              <Select
                value={statusFilter}
                onChange={(_, v) => v !== null && setStatusFilter(v as string)}
              >
                <Option value="all">All Statuses</Option>
                <Option value="active">Active</Option>
                <Option value="pending">Pending</Option>
                <Option value="paused">Paused</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </FormControl>

            {/* Pledge Tier */}
            <FormControl size="sm" sx={{ minWidth: 160 }}>
              <FormLabel>Pledge Tier</FormLabel>
              <Select
                value={tierFilter}
                onChange={(_, v) => v !== null && setTierFilter(v as string)}
              >
                <Option value="all">All Tiers</Option>
                {uniqueTiers.map((t) => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </Select>
            </FormControl>

            {/* Clear */}
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <IconButton
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  onClick={clearFilters}
                  sx={{ alignSelf: 'flex-end', mb: '2px' }}
                >
                  <FilterAltOffRounded />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Sheet>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography level="body-lg" color="neutral">
              No celebrations found for the selected filters.
            </Typography>
          </Box>
        ) : (
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: 'md',
              overflow: 'auto',
              '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
              '--Table-headerUnderlineSize': '1px',
              '--TableCell-paddingX': '12px',
              '--TableCell-paddingY': '10px',
            }}
          >
            <Table
              stickyHeader
              hoverRow
              sx={{
                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                '& tr > *:last-child': { pr: 2 },
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Name</th>
                  <th style={{ width: 130 }}>Contact</th>
                  <th style={{ width: 130 }}>Event</th>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 130 }}>Countdown</th>
                  <th style={{ width: 170 }}>Pledge Tier</th>
                  <th style={{ width: 90 }}>Amount</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, idx) => {
                  const dayInfo = daysLabel(entry.daysUntil);
                  const isToday = entry.daysUntil === 0;
                  return (
                    <tr
                      key={`${entry.enrollmentId}-${entry.kind}-${idx}`}
                      style={isToday ? { backgroundColor: 'var(--joy-palette-warning-50)' } : undefined}
                    >
                      {/* Name + Nitya Seva badge */}
                      <td>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box>
                            <Typography level="title-sm" noWrap>
                              {entry.fullName}
                            </Typography>
                            <Chip
                              size="sm"
                              color="primary"
                              variant="soft"
                              startDecorator={<FavoriteRounded sx={{ fontSize: 10 }} />}
                              sx={{ mt: 0.3, fontSize: '10px' }}
                            >
                              Nitya Seva
                            </Chip>
                          </Box>
                        </Stack>
                      </td>

                      {/* Contact */}
                      <td>
                        <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                          {entry.phone}
                        </Typography>
                        {entry.email && (
                          <Typography level="body-xs" color="neutral" noWrap sx={{ maxWidth: 120 }}>
                            {entry.email}
                          </Typography>
                        )}
                      </td>

                      {/* Event kind */}
                      <td>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={entry.kind === 'birthday' ? 'danger' : 'warning'}
                          startDecorator={
                            entry.kind === 'birthday'
                              ? <CakeRounded sx={{ fontSize: 12 }} />
                              : <FavoriteRounded sx={{ fontSize: 12 }} />
                          }
                        >
                          {entry.kind === 'birthday' ? 'Birthday' : 'Anniversary'}
                        </Chip>
                      </td>

                      {/* Date (month & day) */}
                      <td>
                        <Typography level="body-sm">
                          {dayjs(entry.nextDate).format('DD MMM')}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {dayjs(entry.originalDate).isValid()
                            ? entry.kind === 'birthday'
                              ? `Born ${dayjs(entry.originalDate).format('YYYY')}`
                              : `Since ${dayjs(entry.originalDate).format('YYYY')}`
                            : ''}
                        </Typography>
                      </td>

                      {/* Countdown */}
                      <td>
                        <Chip
                          size="sm"
                          variant={isToday ? 'solid' : 'soft'}
                          color={dayInfo.color}
                        >
                          {dayInfo.label}
                        </Chip>
                      </td>

                      {/* Pledge Tier */}
                      <td>
                        <Typography level="body-sm" noWrap>
                          {entry.pledgeTier}
                        </Typography>
                      </td>

                      {/* Pledge Amount */}
                      <td>
                        <Typography level="body-sm">
                          ₹{entry.pledgeAmount.toLocaleString('en-IN')}
                        </Typography>
                      </td>

                      {/* Status */}
                      <td>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={statusColor(entry.status)}
                        >
                          {entry.status}
                        </Chip>
                      </td>

                      {/* View */}
                      <td>
                        <Tooltip title="View enrollment">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() =>
                              navigate('/nitya-seva/details', {
                                state: { id: entry.enrollmentId },
                              })
                            }
                          >
                            <OpenInNewRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Sheet>
        )}

        {/* Footer note */}
        {!loading && filtered.length > 0 && (
          <Typography level="body-xs" color="neutral" sx={{ textAlign: 'right', pr: 1 }}>
            Showing {filtered.length} celebration{filtered.length !== 1 ? 's' : ''} · All Nitya Seva donors
          </Typography>
        )}
      </Box>
    </Box>
  );
}
